import { CacheManager } from './CacheManager.js';
import { appConfig } from '../../config/appConfig.js';

/**
 * DataCache - Mine data-specific caching implementation
 *
 * Extends generic cache manager with mine data-specific caching patterns,
 * cache invalidation strategies, and specialized cache key generation.
 */
export class DataCache {
  constructor() {
    // Create separate cache managers for different data types
    this.referenceDataCache = new CacheManager({
      ttl: appConfig.cache.ttl, // 15 minutes for reference data
      maxKeys: 50
    });

    this.filterOptionsCache = new CacheManager({
      ttl: appConfig.cache.ttl,
      maxKeys: 20
    });

    // Mine data is not cached due to real-time requirements
    // but we could add viewport-based caching in the future
  }

  /**
   * Cache reference data (countries, commodities)
   *
   * @param {string} type - Data type ('countries', 'commodities')
   * @param {any} data - Data to cache
   */
  setReferenceData(type, data) {
    const key = `reference_${type}`;
    this.referenceDataCache.set(key, data);
  }

  /**
   * Get cached reference data
   *
   * @param {string} type - Data type ('countries', 'commodities')
   * @returns {any|null} Cached data or null if expired/missing
   */
  getReferenceData(type) {
    const key = `reference_${type}`;
    return this.referenceDataCache.get(key);
  }

  /**
   * Cache filter options by type
   *
   * @param {string} filterType - Filter type ('countries', 'commodities', 'mine-types', 'mine-status')
   * @param {Array} options - Filter options array
   */
  setFilterOptions(filterType, options) {
    const key = `filter_options_${filterType}`;
    this.filterOptionsCache.set(key, options);
  }

  /**
   * Get cached filter options
   *
   * @param {string} filterType - Filter type
   * @returns {Array|null} Cached options or null if expired/missing
   */
  getFilterOptions(filterType) {
    const key = `filter_options_${filterType}`;
    return this.filterOptionsCache.get(key);
  }

  /**
   * Cache general filter options response
   *
   * @param {Object} data - Complete filter options data
   */
  setAllFilterOptions(data) {
    const key = 'all_filter_options';
    this.filterOptionsCache.set(key, data);
  }

  /**
   * Get cached general filter options
   *
   * @returns {Object|null} Cached filter options or null
   */
  getAllFilterOptions() {
    const key = 'all_filter_options';
    return this.filterOptionsCache.get(key);
  }

  /**
   * Invalidate reference data cache
   * Useful when data structure changes
   *
   * @param {string} type - Specific type to invalidate, or null for all
   */
  invalidateReferenceData(type = null) {
    if (type) {
      const key = `reference_${type}`;
      this.referenceDataCache.delete(key);
    } else {
      this.referenceDataCache.clear();
    }
  }

  /**
   * Invalidate filter options cache
   *
   * @param {string} filterType - Specific filter type to invalidate, or null for all
   */
  invalidateFilterOptions(filterType = null) {
    if (filterType) {
      const key = `filter_options_${filterType}`;
      this.filterOptionsCache.delete(key);
    } else {
      this.filterOptionsCache.clear();
    }
  }

  /**
   * Clear all cached data
   */
  clearAll() {
    this.referenceDataCache.clear();
    this.filterOptionsCache.clear();
  }

  /**
   * Get comprehensive cache statistics
   *
   * @returns {Object} Combined statistics from all cache managers
   */
  getStats() {
    return {
      referenceData: this.referenceDataCache.getStats(),
      filterOptions: this.filterOptionsCache.getStats(),
      combined: {
        totalSize: this.referenceDataCache.cache.size + this.filterOptionsCache.cache.size,
        totalHits: this.referenceDataCache.metrics.hits + this.filterOptionsCache.metrics.hits,
        totalMisses: this.referenceDataCache.metrics.misses + this.filterOptionsCache.metrics.misses
      }
    };
  }

  /**
   * Perform maintenance cleanup on all caches
   *
   * @returns {Object} Cleanup results
   */
  cleanup() {
    return {
      referenceDataExpired: this.referenceDataCache.cleanup(),
      filterOptionsExpired: this.filterOptionsCache.cleanup()
    };
  }

  /**
   * Check cache health and utilization
   *
   * @returns {Object} Health status
   */
  getHealthStatus() {
    const refUtilization = this.referenceDataCache.getUtilization();
    const filterUtilization = this.filterOptionsCache.getUtilization();

    return {
      status: 'healthy',
      referenceDataUtilization: refUtilization.toFixed(1) + '%',
      filterOptionsUtilization: filterUtilization.toFixed(1) + '%',
      warnings: [
        ...(refUtilization > 90 ? ['Reference data cache near capacity'] : []),
        ...(filterUtilization > 90 ? ['Filter options cache near capacity'] : [])
      ]
    };
  }
}

// Export singleton instance
export const dataCache = new DataCache();